import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../utils/helpers';
import { AdminDrawerMenu } from '../components/AdminDrawerMenu';
import { 
  Users, FileText, Money, SignOut, Headset, Storefront, 
  Plus, Trash, PaperPlaneTilt, ArrowLeft, ChartLine, Crown,
  Gift, Clock, CheckCircle, XCircle, Calendar, Eye, LinkSimple
} from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusColors = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
};

const statusLabels = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [designers, setDesigners] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [marketLinks, setMarketLinks] = useState([]);
  const [subscriptions, setSubscriptions] = useState({ subscriptions: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [selectedDesigner, setSelectedDesigner] = useState(null);
  const [grantDays, setGrantDays] = useState(30);
  const [grantPlan, setGrantPlan] = useState('monthly');
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  const [newLink, setNewLink] = useState({
    title: '',
    description: '',
    url: '',
    category: 'Software',
    image_url: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticketDetail?.messages]);

  const fetchData = async () => {
    try {
      const [statsRes, designersRes, ticketsRes, linksRes, subsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`),
        axios.get(`${API_URL}/admin/designers`),
        axios.get(`${API_URL}/admin/tickets`),
        axios.get(`${API_URL}/admin/market-links`),
        axios.get(`${API_URL}/admin/subscriptions`),
      ]);
      setStats(statsRes.data);
      setDesigners(designersRes.data);
      setTickets(ticketsRes.data);
      setMarketLinks(linksRes.data);
      setSubscriptions(subsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
      if (error.response?.status === 403 || error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExtendSubscription = async (designerId, days) => {
    try {
      await axios.post(`${API_URL}/admin/subscriptions/${designerId}/extend?days=${days}`);
      toast.success(`Subscription extended by ${days} days`);
      fetchData();
    } catch (error) {
      toast.error('Failed to extend subscription');
    }
  };

  const handleCancelSubscription = async (designerId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    try {
      await axios.post(`${API_URL}/admin/subscriptions/${designerId}/cancel`);
      toast.success('Subscription cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const handleGrantSubscription = async () => {
    if (!selectedDesigner) return;
    try {
      await axios.post(`${API_URL}/admin/subscriptions/${selectedDesigner}/grant?plan=${grantPlan}&days=${grantDays}`);
      toast.success(`Subscription granted for ${grantDays} days`);
      setGrantDialogOpen(false);
      setSelectedDesigner(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to grant subscription');
    }
  };

  const fetchTicketDetail = async (ticketId) => {
    try {
      const response = await axios.get(`${API_URL}/admin/tickets/${ticketId}`);
      setTicketDetail(response.data);
    } catch (error) {
      toast.error('Failed to load ticket');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    try {
      await axios.post(`${API_URL}/admin/tickets/${selectedTicket}/messages`, { message: newMessage });
      setNewMessage('');
      fetchTicketDetail(selectedTicket);
      fetchData(); // Refresh tickets list
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleUpdateStatus = async (ticketId, status) => {
    try {
      await axios.put(`${API_URL}/admin/tickets/${ticketId}/status?status=${status}`);
      toast.success('Status updated');
      fetchTicketDetail(ticketId);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/admin/market-links`, newLink);
      toast.success('Link added successfully!');
      setLinkDialogOpen(false);
      setNewLink({ title: '', description: '', url: '', category: 'Software', image_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add link');
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;
    try {
      await axios.delete(`${API_URL}/admin/market-links/${linkId}`);
      toast.success('Link deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete link');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('user_role');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#6B7280]">Loading...</div>
      </div>
    );
  }

  const openTickets = tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length;

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSelectedTicket(null);
    setTicketDetail(null);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard">
      {/* Admin Drawer Menu */}
      <AdminDrawerMenu 
        activeTab={activeTab} 
        onTabChange={handleTabChange}
        onLogout={handleLogout}
        ticketCount={openTickets}
      />
      
      {/* Main Content */}
      <div className="p-4 md:p-6 lg:p-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            <div className="mb-8">
              <h2 className="font-['Chivo'] text-3xl font-black tracking-tighter text-[#121212] mb-2">
                Platform Overview
              </h2>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="border-[#E5E7EB] bg-white">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-[#6B7280]">Total Designers</CardTitle>
                  <div className="bg-blue-50 p-2 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" weight="duotone" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-black font-['Chivo']">{stats?.total_designers || 0}</div>
                </CardContent>
              </Card>
              <Card className="border-[#E5E7EB] bg-white">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-[#6B7280]">Total Customers</CardTitle>
                  <div className="bg-green-50 p-2 rounded-lg">
                    <Users className="h-5 w-5 text-green-600" weight="duotone" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-black font-['Chivo']">{stats?.total_customers || 0}</div>
                </CardContent>
              </Card>
              <Card className="border-[#E5E7EB] bg-white">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-[#6B7280]">Total Invoices</CardTitle>
                  <div className="bg-purple-50 p-2 rounded-lg">
                    <FileText className="h-5 w-5 text-purple-600" weight="duotone" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-black font-['Chivo']">{stats?.total_invoices || 0}</div>
                </CardContent>
              </Card>
              <Card className="border-[#E5E7EB] bg-white">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-[#6B7280]">Platform Revenue</CardTitle>
                  <div className="bg-orange-50 p-2 rounded-lg">
                    <Money className="h-5 w-5 text-[#FF5A36]" weight="duotone" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-black font-['Chivo']">{formatCurrency(stats?.total_revenue || 0)}</div>
                </CardContent>
              </Card>
            </div>

            {/* Designers List */}
            <Card className="border-[#E5E7EB] bg-white">
              <CardHeader>
                <CardTitle className="font-['Chivo'] text-xl font-bold">Recent Designers ({designers.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {designers.length === 0 ? (
                  <p className="text-center text-[#6B7280] py-8">No designers registered yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#FAFAFA] border-b border-[#E5E7EB]">
                        <tr>
                          <th className="text-left p-4 text-sm font-medium text-[#6B7280]">Designer</th>
                          <th className="text-left p-4 text-sm font-medium text-[#6B7280]">Location</th>
                          <th className="text-left p-4 text-sm font-medium text-[#6B7280]">Status</th>
                          <th className="text-left p-4 text-sm font-medium text-[#6B7280]">Joined</th>
                          <th className="text-left p-4 text-sm font-medium text-[#6B7280]">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {designers.slice(0, 10).map((designer) => (
                          <tr key={designer.id} className="border-b border-[#E5E7EB] hover:bg-[#FAFAFA]">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-bold">
                                  {designer.name?.charAt(0).toUpperCase() || 'D'}
                                </div>
                                <div>
                                  <p className="font-medium text-[#121212]">{designer.name}</p>
                                  <p className="text-xs text-[#6B7280]">@{designer.profile_id || designer.id.slice(0, 8)}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4 text-[#6B7280]">{designer.location || '-'}</td>
                            <td className="p-4">
                              {designer.is_premium ? (
                                <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0">
                                  <Crown className="h-3 w-3 mr-1" weight="fill" /> Premium
                                </Badge>
                              ) : (
                                <Badge variant="outline">Free</Badge>
                              )}
                            </td>
                            <td className="p-4 text-[#6B7280] text-sm">{formatDate(designer.created_at)}</td>
                            <td className="p-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(`/${designer.profile_id || designer.id}`, '_blank')}
                                className="h-8"
                              >
                                <Eye className="h-4 w-4 mr-1" weight="bold" />
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <>
            {selectedTicket && ticketDetail ? (
              // Ticket Chat View
              <>
                <Button variant="ghost" onClick={() => { setSelectedTicket(null); setTicketDetail(null); }} className="mb-4">
                  <ArrowLeft className="h-5 w-5 mr-2" /> Back to Tickets
                </Button>

                <Card className="border-[#E5E7EB]">
                  <CardHeader className="border-b border-[#E5E7EB]">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <CardTitle className="font-['Chivo'] text-xl">{ticketDetail.ticket.subject}</CardTitle>
                        <p className="text-sm text-gray-500 mt-1">
                          By: {ticketDetail.ticket.designer_name} | {formatDate(ticketDetail.ticket.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select 
                          value={ticketDetail.ticket.status} 
                          onValueChange={(v) => handleUpdateStatus(selectedTicket, v)}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-gray-50">
                      {ticketDetail.messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg p-3 ${
                              msg.sender_type === 'admin'
                                ? 'bg-[#FF5A36] text-white rounded-br-none'
                                : 'bg-white border border-gray-200 rounded-bl-none'
                            }`}
                          >
                            <p className={`text-xs font-medium mb-1 ${msg.sender_type === 'admin' ? 'text-orange-100' : 'text-gray-500'}`}>
                              {msg.sender_name}
                            </p>
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                            <p className={`text-xs mt-1 ${msg.sender_type === 'admin' ? 'text-orange-200' : 'text-gray-400'}`}>
                              {new Date(msg.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>

                    {ticketDetail.ticket.status !== 'closed' && (
                      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 flex gap-2">
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your reply..."
                          className="flex-1"
                        />
                        <Button type="submit" disabled={!newMessage.trim()} className="bg-[#FF5A36] hover:bg-[#E04828]">
                          <PaperPlaneTilt className="h-5 w-5" weight="fill" />
                        </Button>
                      </form>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              // Tickets List View
              <>
                <div className="mb-6">
                  <h2 className="font-['Chivo'] text-3xl font-black tracking-tighter text-[#121212]">Support Tickets</h2>
                  <p className="text-[#6B7280] mt-1">Manage designer support requests</p>
                </div>

                <Card className="border-[#E5E7EB]">
                  <CardContent className="pt-6">
                    {tickets.length === 0 ? (
                      <div className="text-center py-12">
                        <Headset className="h-16 w-16 text-gray-300 mx-auto mb-4" weight="duotone" />
                        <p className="text-gray-500">No tickets yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {tickets.map((ticket) => (
                          <div
                            key={ticket.id}
                            onClick={() => { setSelectedTicket(ticket.id); fetchTicketDetail(ticket.id); }}
                            className="p-4 border border-gray-200 rounded-lg hover:border-[#FF5A36] hover:bg-orange-50/50 cursor-pointer transition-all"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-900">{ticket.subject}</h4>
                                <p className="text-sm text-gray-500 mt-1">
                                  {ticket.designer_name} • {formatDate(ticket.updated_at)}
                                </p>
                              </div>
                              <Badge className={statusColors[ticket.status]}>{statusLabels[ticket.status]}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}

        {/* Market Links Tab */}
        {activeTab === 'market' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-['Chivo'] text-3xl font-black tracking-tighter text-[#121212]">Market Links</h2>
                <p className="text-[#6B7280] mt-1">Manage affiliate and useful links for designers</p>
              </div>
              
              <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">
                    <Plus className="h-5 w-5 mr-2" weight="bold" />
                    Add Link
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Market Link</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateLink} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={newLink.title}
                        onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                        placeholder="Link title"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>URL *</Label>
                      <Input
                        value={newLink.url}
                        onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                        placeholder="https://example.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select value={newLink.category} onValueChange={(v) => setNewLink({ ...newLink, category: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Software">Software</SelectItem>
                          <SelectItem value="Website">Website</SelectItem>
                          <SelectItem value="Tools">Tools</SelectItem>
                          <SelectItem value="Amazon">Amazon</SelectItem>
                          <SelectItem value="Affiliate">Affiliate</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={newLink.description}
                        onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                        placeholder="Brief description..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Image URL (Optional)</Label>
                      <Input
                        value={newLink.image_url}
                        onChange={(e) => setNewLink({ ...newLink, image_url: e.target.value })}
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">
                      Add Link
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <Card className="border-[#E5E7EB]">
              <CardContent className="pt-6">
                {marketLinks.length === 0 ? (
                  <div className="text-center py-12">
                    <Storefront className="h-16 w-16 text-gray-300 mx-auto mb-4" weight="duotone" />
                    <p className="text-gray-500">No links added yet</p>
                    <p className="text-sm text-gray-400 mt-1">Add useful links for your designers</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {marketLinks.map((link) => (
                      <div key={link.id} className="p-4 border border-gray-200 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {link.image_url && (
                            <img src={link.image_url} alt={link.title} className="w-16 h-16 object-cover rounded-lg" />
                          )}
                          <div>
                            <h4 className="font-medium text-gray-900">{link.title}</h4>
                            <p className="text-sm text-gray-500">{link.description}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline">{link.category}</Badge>
                              <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#FF5A36] hover:underline">
                                {link.url.substring(0, 40)}...
                              </a>
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteLink(link.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash className="h-5 w-5" weight="bold" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Designers Tab */}
        {activeTab === 'designers' && (
          <>
            <div className="mb-6">
              <h2 className="font-['Chivo'] text-3xl font-black tracking-tighter text-[#121212]">All Designers</h2>
              <p className="text-[#6B7280] mt-1">Platform वर registered सर्व designers ({designers.length})</p>
            </div>

            <Card className="border-[#E5E7EB] bg-white">
              <CardContent className="pt-6">
                {designers.length === 0 ? (
                  <p className="text-center text-[#6B7280] py-8">No designers registered yet</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {designers.map((designer) => (
                      <Card key={designer.id} className="border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all">
                        <CardContent className="pt-4">
                          <div className="flex items-start gap-3">
                            {/* Avatar */}
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                              {designer.name?.charAt(0).toUpperCase() || 'D'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-bold text-gray-900 truncate">{designer.name}</h4>
                              <p className="text-xs text-gray-500 truncate">@{designer.profile_id || designer.id.slice(0, 8)}</p>
                              <p className="text-xs text-gray-400 truncate">{designer.email}</p>
                            </div>
                          </div>
                          
                          <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <p className="text-gray-400">Location</p>
                              <p className="text-gray-700 font-medium truncate">{designer.location || '-'}</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Mobile</p>
                              <p className="text-gray-700 font-medium">{designer.mobile}</p>
                            </div>
                          </div>

                          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              {designer.is_premium ? (
                                <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 text-xs">
                                  <Crown className="h-3 w-3 mr-1" weight="fill" /> Premium
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">Free</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const link = `${window.location.origin}/${designer.profile_id || designer.id}`;
                                  navigator.clipboard.writeText(link);
                                  toast.success('Link copied!');
                                }}
                                className="h-8 px-2"
                                title="Copy Profile Link"
                              >
                                <LinkSimple className="h-4 w-4" weight="bold" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => window.open(`/${designer.profile_id || designer.id}`, '_blank')}
                                className="bg-[#FF5A36] hover:bg-[#E04828] text-white h-8 px-3"
                              >
                                <Eye className="h-4 w-4 mr-1" weight="bold" />
                                Profile
                              </Button>
                            </div>
                          </div>

                          <p className="text-xs text-gray-400 mt-2">
                            Joined: {formatDate(designer.created_at)}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
